
import React, { useState, useRef, useEffect } from 'react';
import { debugLogger } from '@/utils/debugLogger';
import { imageUrlService, ImageContext } from '@/services/ImageUrlService';
import { useImageUrl } from '@/hooks/useImageUrl';
import { Trash2, Star } from 'lucide-react';

interface OptimizedLazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
  // Remove functionality
  showRemoveButton?: boolean;
  onRemove?: () => void;
  // Set as main functionality
  showSetMainButton?: boolean;
  onSetMain?: () => void;
  isMainPhoto?: boolean;
  // Lazy loading control
  lazy?: boolean;
  // Image variant context
  context?: ImageContext;
  // Disable blur effect for simple rendering
  disableBlur?: boolean;
}

export const OptimizedLazyImage: React.FC<OptimizedLazyImageProps> = ({
  src,
  alt,
  className = '',
  placeholder,
  onLoad,
  onError,
  showRemoveButton = false,
  onRemove,
  showSetMainButton = false,
  onSetMain,
  isMainPhoto = false,
  lazy = true,
  context = 'thumbnail',
  disableBlur = false
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy); // Load immediately if not lazy
  const [hasError, setHasError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [fallbackUrls, setFallbackUrls] = useState<string[]>([]);
  const [currentFallbackIndex, setCurrentFallbackIndex] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get optimized URL using async hook (supports v1.5 signed URLs)
  const { url: processedUrl, isLoading: urlLoading } = useImageUrl(src, context);

  // Update current URL and fallbacks when processed URL is ready
  useEffect(() => {
    if (!processedUrl) return;
    
    const fallbacks = imageUrlService.generateFallbackUrls(src, context);
    
    setCurrentUrl(processedUrl);
    setFallbackUrls(fallbacks);
    setCurrentFallbackIndex(0);
    setHasError(false);
    setIsLoaded(false);
    
    debugLogger.debug('OptimizedLazyImage', 'URL processed', {
      original: src,
      processed: processedUrl,
      fallbacks,
      context
    });
  }, [processedUrl, src, context]);

  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (!imgRef.current || !lazy) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [currentUrl, lazy]);

  const handleLoad = () => {
    setIsLoaded(true);
    debugLogger.debug('OptimizedLazyImage', 'Image loaded', { 
      url: currentUrl, 
      original: src,
      context 
    });
    onLoad?.();
  };

  const handleError = () => {
    debugLogger.warn('OptimizedLazyImage', 'Image load failed', { 
      url: currentUrl, 
      original: src,
      fallbackIndex: currentFallbackIndex,
      totalFallbacks: fallbackUrls.length 
    });
    
    // Try next fallback URL if available
    if (currentFallbackIndex < fallbackUrls.length) {
      const nextUrl = fallbackUrls[currentFallbackIndex];
      setCurrentUrl(nextUrl);
      setCurrentFallbackIndex(prev => prev + 1);
      setIsLoaded(false);
      debugLogger.debug('OptimizedLazyImage', 'Trying fallback URL', { 
        nextUrl, 
        fallbackIndex: currentFallbackIndex + 1 
      });
      return;
    }
    
    // All URLs failed
    setHasError(true);
    onError?.();
  };

  const getCleanClassName = () => {
    // Remove object-fit classes to avoid conflicts
    return className.replace(/object-(contain|cover|fill|scale-down|none)/g, '').trim();
  };

  return (
    <div className="relative h-full w-full"> {/* Wrapper */}
      <div 
        ref={imgRef} 
        className={`relative overflow-hidden h-full w-full ${getCleanClassName()}`}
      >
        {!isInView && (
          <div className="absolute inset-0 bg-black/20 animate-pulse z-10">
            {placeholder || (
              <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
            )}
          </div>
        )}
        
        {isInView && !hasError && currentUrl && (
          <>
            {disableBlur ? (
              // Simple mode: just a standard image
              <img
                src={currentUrl}
                alt={alt}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  isLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={handleLoad}
                onError={handleError}
                loading="lazy"
              />
            ) : (
              // Complex mode: blur + sharp layers
              <>
                {/* Blurred background layer */}
                <div
                  className={`absolute inset-0 transition-opacity duration-300 pointer-events-none ${
                    isLoaded ? 'opacity-0' : 'opacity-100'
                  }`}
                  style={{
                    backgroundImage: `url(${currentUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(3px)',
                    willChange: 'transform',
                  }}
                />
                
                {/* Sharp foreground layer */}
        <div
          className={`absolute inset-0 transition-opacity duration-300 z-10 pointer-events-none ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
                  style={{
                    backgroundImage: `url(${currentUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                
                {/* Hidden image for loading detection */}
                <img
                  src={currentUrl}
                  alt={alt}
                  className="absolute opacity-0 pointer-events-none"
                  onLoad={handleLoad}
                  onError={handleError}
                  loading="lazy"
                />
              </>
            )}
          </>
        )}
        
        {hasError && (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center z-10">
            {placeholder || (
              <div className="text-center p-4">
                <p className="text-sm text-gray-500">Foto indisponível</p>
                <p className="text-xs text-gray-400 mt-1">Tente novamente mais tarde</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main photo indicator - positioned outside image container for proper stacking */}
      {isMainPhoto && (
        <div className="absolute top-2 left-2 z-50">
          <div className="flex items-center gap-1 bg-luxury-gold/90 text-black px-2 py-1 rounded text-xs font-medium">
            <Star className="h-3 w-3 fill-current" />
            Principal
          </div>
        </div>
      )}

      {/* Remove button - positioned outside image container for proper stacking */}
      {showRemoveButton && onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 z-50 bg-red-500/90 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors duration-200"
          aria-label="Remover foto"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}

      {/* Set as main button - positioned outside image container for proper stacking */}
      {showSetMainButton && onSetMain && !isMainPhoto && (
        <button
          onClick={onSetMain}
          className="absolute bottom-2 left-2 z-50 bg-white/90 hover:bg-white text-black px-2 py-1 rounded text-xs transition-colors duration-200"
          aria-label="Definir como principal"
        >
          <Star className="h-3 w-3 inline mr-1" />
          Principal
        </button>
      )}
    </div>
  );
};

export default OptimizedLazyImage;
